import { FluidCanvas, FluidCanvasState } from "@kalyzee/kalyzee-ui";
import clsx from "clsx";
import React, { createContext, useContext, useMemo, useRef } from "react";
import styles from "./AnimatedBackground.module.css";

interface AnimatedBackgroundContextValue {
  stateRef: React.MutableRefObject<FluidCanvasState | undefined>;
}

/**
 * Storage for the FluidCanvas snapshot, kept in a ref so reads/writes don't
 * trigger renders. The provider sits high enough in the tree that it
 * outlives individual page mounts; that's what lets the canvas pick up
 * where it left off after a navigation.
 */
const AnimatedBackgroundContext = createContext<AnimatedBackgroundContextValue | null>(null);

export const AnimatedBackgroundProvider = ({ children }: { children: React.ReactNode }) => {
  const stateRef = useRef<FluidCanvasState | undefined>();
  const value = useMemo<AnimatedBackgroundContextValue>(() => ({ stateRef }), []);
  return (
    <AnimatedBackgroundContext.Provider value={value}>
      {children}
    </AnimatedBackgroundContext.Provider>
  );
};

export interface AnimatedBackgroundProps {
  children?: React.ReactNode;
  /** Class on the outer wrapper. The FluidCanvas is positioned inside it. */
  className?: string;
  /**
   * Class on the inner content div that holds `children`. Use this when the
   * children rely on flex/grid distribution: putting those rules on the
   * outer wrapper would target the FluidCanvas + content pair instead.
   */
  contentClassName?: string;
  /** Animation speed (forwarded to FluidCanvas). */
  speed?: number;
  /** Orb size class (forwarded to FluidCanvas). */
  shapeSize?: "small" | "medium" | "big";
}

/**
 * Wraps `children` with a FluidCanvas background. When mounted under an
 * `AnimatedBackgroundProvider`, the canvas resumes from the last snapshot
 * stored in the provider's ref — so navigating between pages that both use
 * AnimatedBackground keeps the orbs flowing instead of restarting them.
 */
export const AnimatedBackground = ({
  children,
  className,
  contentClassName,
  speed = 1,
  shapeSize = "big",
}: AnimatedBackgroundProps) => {
  const ctx = useContext(AnimatedBackgroundContext);

  return (
    <div className={clsx(styles.container, className)}>
      <div className={styles.fluidBg}>
        <FluidCanvas
          speed={speed}
          shapeSize={shapeSize}
          stateRef={ctx?.stateRef}
          fps={10}
          colorPalette={{
            //light: ["#A8E6CF", "#C4B8F5", "#F8B8D4"], // pastel
            light: ["#17BF88", "#513BF1", "#EC4899"],
            dark: ["#17BF88", "#513BF1", "#EC4899"],
          }}
        />
      </div>
      <div className={clsx(styles.content, contentClassName)}>{children}</div>
    </div>
  );
};
